import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Search, Pencil, Trash2, AlertCircle, Stethoscope, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'cardiovascular', label: 'Cardiovascular', color: 'bg-red-100 text-red-800' },
  { value: 'respiratorio', label: 'Respiratorio', color: 'bg-blue-100 text-blue-800' },
  { value: 'neurologico', label: 'Neurológico', color: 'bg-purple-100 text-purple-800' },
  { value: 'gastrointestinal', label: 'Gastrointestinal', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'trauma', label: 'Trauma', color: 'bg-orange-100 text-orange-800' },
  { value: 'infeccioso', label: 'Infeccioso', color: 'bg-green-100 text-green-800' },
  { value: 'metabolico', label: 'Metabólico', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'renal', label: 'Renal', color: 'bg-amber-100 text-amber-800' },
  { value: 'hematologico', label: 'Hematológico', color: 'bg-pink-100 text-pink-800' },
  { value: 'otro', label: 'Otro', color: 'bg-gray-100 text-gray-800' },
];

const URGENCY_LEVELS = [
  { value: 'alta', label: 'Alta', color: 'bg-red-500 text-white' },
  { value: 'media', label: 'Media', color: 'bg-yellow-500 text-white' },
  { value: 'baja', label: 'Baja', color: 'bg-green-500 text-white' },
];

const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[CATEGORIES.length - 1];
const getUrgencyInfo = (urg) => URGENCY_LEVELS.find(u => u.value === urg) || URGENCY_LEVELS[1];

export default function DiagnosesPage() {
  const { user, hasPermission, isSuperAdmin, isInstructor } = useAuth();
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDiagnosis, setEditingDiagnosis] = useState(null);
  const [formData, setFormData] = useState({
    codigo_cie10: '',
    nombre: '',
    nombre_corto: '',
    categoria: 'otro',
    descripcion: '',
    criterios_diagnosticos: [],
    diagnosticos_diferenciales: [],
    urgencia: 'media'
  });
  const [criterioInput, setCriterioInput] = useState('');
  const [diferencialInput, setDiferencialInput] = useState('');

  const canCreate = hasPermission('diagnosticos', 'crear') || isSuperAdmin || isInstructor;
  const canEdit = hasPermission('diagnosticos', 'editar') || isSuperAdmin || isInstructor;
  const canDelete = hasPermission('diagnosticos', 'eliminar') || isSuperAdmin;

  const fetchDiagnoses = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/diagnoses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnoses(response.data);
    } catch (error) {
      toast.error('Error al cargar diagnósticos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDiagnoses();
  }, [fetchDiagnoses]);

  const handleOpenModal = (diagnosis = null) => {
    if (diagnosis) {
      setEditingDiagnosis(diagnosis);
      setFormData({
        codigo_cie10: diagnosis.codigo_cie10 || '',
        nombre: diagnosis.nombre || '',
        nombre_corto: diagnosis.nombre_corto || '',
        categoria: diagnosis.categoria || 'otro',
        descripcion: diagnosis.descripcion || '',
        criterios_diagnosticos: diagnosis.criterios_diagnosticos || [],
        diagnosticos_diferenciales: diagnosis.diagnosticos_diferenciales || [],
        urgencia: diagnosis.urgencia || 'media'
      });
    } else {
      setEditingDiagnosis(null);
      setFormData({
        codigo_cie10: '',
        nombre: '',
        nombre_corto: '',
        categoria: 'otro',
        descripcion: '',
        criterios_diagnosticos: [],
        diagnosticos_diferenciales: [],
        urgencia: 'media'
      });
    }
    setCriterioInput('');
    setDiferencialInput('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDiagnosis(null);
  };

  const handleAddCriterio = () => {
    if (criterioInput.trim()) {
      setFormData(prev => ({
        ...prev,
        criterios_diagnosticos: [...prev.criterios_diagnosticos, criterioInput.trim()]
      }));
      setCriterioInput('');
    }
  };

  const handleRemoveCriterio = (index) => {
    setFormData(prev => ({
      ...prev,
      criterios_diagnosticos: prev.criterios_diagnosticos.filter((_, i) => i !== index)
    }));
  };

  const handleAddDiferencial = () => {
    if (diferencialInput.trim()) {
      setFormData(prev => ({
        ...prev,
        diagnosticos_diferenciales: [...prev.diagnosticos_diferenciales, diferencialInput.trim()]
      }));
      setDiferencialInput('');
    }
  };

  const handleRemoveDiferencial = (index) => {
    setFormData(prev => ({
      ...prev,
      diagnosticos_diferenciales: prev.diagnosticos_diferenciales.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingDiagnosis) {
        await axios.put(`${API_URL}/api/diagnoses/${editingDiagnosis.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Diagnóstico actualizado');
      } else {
        await axios.post(`${API_URL}/api/diagnoses`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Diagnóstico creado');
      }
      handleCloseModal();
      fetchDiagnoses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar diagnóstico');
    }
  };

  const handleDelete = async (diagnosis) => {
    if (diagnosis.is_default) {
      toast.error('No se pueden eliminar diagnósticos del sistema');
      return;
    }
    if (!window.confirm(`¿Eliminar diagnóstico "${diagnosis.nombre}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/diagnoses/${diagnosis.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Diagnóstico eliminado');
      fetchDiagnoses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar diagnóstico');
    }
  };

  const filteredDiagnoses = diagnoses.filter(d => {
    const matchesSearch = (d.nombre || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
      (d.codigo_cie10 || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
      (d.nombre_corto && d.nombre_corto.toLowerCase().includes((searchTerm || '').toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || d.categoria === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-6 space-y-6" data-testid="diagnoses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Stethoscope className="h-7 w-7 text-blue-600" />
            Catálogo de Diagnósticos
          </h1>
          <p className="text-gray-500">Gestión de diagnósticos CIE-10 para simulaciones</p>
        </div>
        {canCreate && (
          <Button onClick={() => handleOpenModal()} data-testid="add-diagnosis-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Diagnóstico
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre o código CIE-10..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-diagnoses"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]" data-testid="category-filter">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Diagnoses Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 h-48 bg-gray-100" />
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDiagnoses.map(diagnosis => {
            const catInfo = getCategoryInfo(diagnosis.categoria);
            const urgInfo = getUrgencyInfo(diagnosis.urgencia);
            return (
              <Card key={diagnosis.id} className="hover:shadow-md transition-shadow" data-testid={`diagnosis-card-${diagnosis.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <Badge variant="outline" className="mb-2 font-mono text-xs">
                        {diagnosis.codigo_cie10}
                      </Badge>
                      <CardTitle className="text-base leading-tight">
                        {diagnosis.nombre_corto || diagnosis.nombre}
                      </CardTitle>
                      {diagnosis.nombre_corto && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{diagnosis.nombre}</p>
                      )}
                    </div>
                    <Badge className={urgInfo.color}>{urgInfo.label}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="space-y-2">
                    <Badge variant="secondary" className={catInfo.color}>
                      {catInfo.label}
                    </Badge>
                    
                    {diagnosis.descripcion && (
                      <p className="text-sm text-gray-600 line-clamp-2">{diagnosis.descripcion}</p>
                    )}
                    
                    {diagnosis.criterios_diagnosticos?.length > 0 && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">{diagnosis.criterios_diagnosticos.length} criterios</span>
                      </div>
                    )}

                    {diagnosis.is_default && (
                      <Badge variant="outline" className="text-xs">Sistema</Badge>
                    )}

                    <div className="flex gap-2 pt-2">
                      {canEdit && !diagnosis.is_default && (
                        <Button variant="outline" size="sm" onClick={() => handleOpenModal(diagnosis)}>
                          <Pencil className="h-3 w-3 mr-1" />
                          Editar
                        </Button>
                      )}
                      {canDelete && !diagnosis.is_default && (
                        <Button variant="outline" size="sm" onClick={() => handleDelete(diagnosis)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-3 w-3 mr-1" />
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && filteredDiagnoses.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No se encontraron diagnósticos</p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDiagnosis ? 'Editar Diagnóstico' : 'Nuevo Diagnóstico'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código CIE-10 *</Label>
                <Input
                  value={formData.codigo_cie10}
                  onChange={(e) => setFormData(prev => ({ ...prev, codigo_cie10: e.target.value.toUpperCase() }))}
                  placeholder="Ej: I21.0"
                  required
                  data-testid="input-codigo-cie10"
                />
              </div>
              <div>
                <Label>Categoría *</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData(prev => ({ ...prev, categoria: v }))}>
                  <SelectTrigger data-testid="select-categoria">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Nombre Completo *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Ej: Infarto agudo de miocardio de la pared anterior"
                required
                data-testid="input-nombre"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre Corto</Label>
                <Input
                  value={formData.nombre_corto}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre_corto: e.target.value }))}
                  placeholder="Ej: IAM anterior"
                  data-testid="input-nombre-corto"
                />
              </div>
              <div>
                <Label>Urgencia</Label>
                <Select value={formData.urgencia} onValueChange={(v) => setFormData(prev => ({ ...prev, urgencia: v }))}>
                  <SelectTrigger data-testid="select-urgencia">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {URGENCY_LEVELS.map(urg => (
                      <SelectItem key={urg.value} value={urg.value}>{urg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción clínica del diagnóstico"
                rows={2}
                data-testid="input-descripcion"
              />
            </div>

            {/* Criterios diagnósticos */}
            <div>
              <Label>Criterios Diagnósticos</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={criterioInput}
                  onChange={(e) => setCriterioInput(e.target.value)}
                  placeholder="Agregar criterio"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCriterio())}
                />
                <Button type="button" variant="outline" onClick={handleAddCriterio}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.criterios_diagnosticos.map((criterio, idx) => (
                  <Badge key={idx} variant="secondary" className="pr-1">
                    {criterio}
                    <button type="button" onClick={() => handleRemoveCriterio(idx)} className="ml-2 hover:text-red-500">×</button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Diagnósticos diferenciales */}
            <div>
              <Label>Diagnósticos Diferenciales</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={diferencialInput}
                  onChange={(e) => setDiferencialInput(e.target.value)}
                  placeholder="Agregar diferencial"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDiferencial())}
                />
                <Button type="button" variant="outline" onClick={handleAddDiferencial}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.diagnosticos_diferenciales.map((dif, idx) => (
                  <Badge key={idx} variant="outline" className="pr-1">
                    {dif}
                    <button type="button" onClick={() => handleRemoveDiferencial(idx)} className="ml-2 hover:text-red-500">×</button>
                  </Badge>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-diagnosis">
                {editingDiagnosis ? 'Guardar Cambios' : 'Crear Diagnóstico'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
